import { useState } from "react";
import { Outlet, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DATE_STYLES, fmtDate, fmtStamp, todayIn } from "@shared/dates";
import { api, ApiError, errorMessage } from "@/lib/api";
import { useChannelLook } from "@/lib/colors";
import { useScope, useUser } from "@/lib/queries";
import { useConnection } from "@/stores/connection";
import { draftHasContent, useDraft } from "@/stores/draft";
import { queuedCount, useOutbox } from "@/stores/outbox";
import { useSession } from "@/stores/session";
import { useUi } from "@/stores/ui";
import { BookingDetailHost } from "@/features/booking/BookingDetail";
import { Dialog } from "../overlays";
import { Button, Input } from "../ui";
import { CommandPalette } from "./CommandPalette";
import { Header } from "./Header";
import { BottomBar, CourseNav } from "./Nav";
import { AreaFrame } from "./AreaFrame";
import { useRememberSection } from "@/lib/nav";
import { endpoints } from "@/lib/endpoints";
import { queryKeys } from "@/lib/queryKeys";
import { usePrefetchMyChecks } from "@/lib/upkeepPrefetch";

export function AppShell() {
  const dateStyle = useUi((s) => s.dateStyle);
  usePrefetchMyChecks();
  useRememberSection();
  return (
    <div className="flex min-h-dvh flex-col">
      {/* The brand band: 6px of cream, the top course of the wall. */}
      <div
        data-print="hide"
        className="h-1.5 border-b border-line-strong bg-surface-brand"
      />
      <div data-print="hide" className="sticky top-0 z-40">
        <Header />
        <CourseNav />
      </div>
      <div data-print="hide">
        <OfflineBanner />
      </div>
      {/* Keyed on the date style: switching it redraws every date on the page at once. */}
      <main
        key={dateStyle}
        className="flex-1 pb-[calc(84px+env(safe-area-inset-bottom))] min-[900px]:pb-0"
      >
        <AreaFrame>
          <Outlet />
        </AreaFrame>
      </main>
      {/* Printing a report prints the report, not the app around it. */}
      <div data-print="hide">
        <Footer />
        <BottomBar />
      </div>
      <BookingDetailHost />
      <CommandPalette />
      <SessionExpiredHost />
    </div>
  );
}

function OfflineBanner() {
  const online = useConnection((s) => s.online);
  const items = useOutbox((s) => s.items);
  const n = queuedCount(items);
  const failed = items.filter((i) => i.status === "failed");
  if (online && failed.length === 0) return null;
  return (
    <div className="border-b border-line-strong">
      {!online && (
        <div className="flex flex-wrap items-center gap-2.5 bg-brass px-4 py-2 text-[13px] font-semibold text-brass-ink sm:px-5">
          <span className="border border-black/35 px-1.5 py-px font-mono text-[11px]">
            OFFLINE
          </span>
          Working offline. {n} action{n === 1 ? "" : "s"} queued — saved on this
          device and sent when the connection returns.
        </div>
      )}
      {failed.map((f) => (
        <FailedRow key={f.key} itemKey={f.key} />
      ))}
    </div>
  );
}

function FailedRow({ itemKey }: { itemKey: string }) {
  const item = useOutbox((s) => s.items.find((i) => i.key === itemKey));
  const discard = useOutbox((s) => s.discard);
  if (!item) return null;
  return (
    <div className="flex flex-wrap items-center gap-2.5 border-t border-line bg-surface px-4 py-2 text-[13px] sm:px-5">
      <span className="bg-rhodo px-1.5 py-px font-mono text-[11px] text-[#FFFDF8]">
        NOT SAVED
      </span>
      <span className="flex-1">
        {item.payload.guestName || item.payload.guestPhone || "Booking"} ·{" "}
        {item.roomName} · {fmtDate(item.payload.checkIn)} — {item.error}
      </span>
      <Button size="sm" onClick={() => discard(itemKey)}>
        Dismiss
      </Button>
    </div>
  );
}

function Footer() {
  const { property, primary } = useScope();
  const p = property ?? primary;
  const dateStyle = useUi((s) => s.dateStyle);
  // The API's own version, from its health check: a mismatch after a deploy shows up here.
  const health = useQuery({
    queryKey: queryKeys.health.all,
    queryFn: () => api<{ version?: string }>(endpoints.health),
    staleTime: 10 * 60_000,
    retry: false,
  });
  return (
    <footer className="hidden flex-wrap items-center gap-x-7 gap-y-3 border-t border-line-strong bg-surface px-[22px] py-5 font-mono text-[11px] tracking-[.06em] text-subtle min-[900px]:flex">
      <span>
        KATHKUNI PMS
        {p
          ? ` · ${p.locationName.toUpperCase()}, ${p.region.toUpperCase()}`
          : ""}
      </span>
      <span>{fmtStamp(todayIn(p?.timezone))}</span>
      <span className="flex-1" />
      <span>₹ INR · {DATE_STYLES[dateStyle].full}</span>
      <span title="Web app and API versions">
        WEB v{__APP_VERSION__}
        {health.data?.version ? ` · API v${health.data.version}` : ""}
      </span>
    </footer>
  );
}

/**
 * Session expired mid-shift. Losing a half-filled booking to a timeout is
 * unacceptable on this connection: the draft and the outbox are kept, and
 * unlocking sends them.
 */
function SessionExpiredHost() {
  const expired = useSession((s) => s.expired);
  const restore = useSession((s) => s.restore);
  const me = useUser();
  const draft = useDraft();
  const { lookup } = useChannelLook();
  const items = useOutbox((s) => s.items);
  const flush = useOutbox((s) => s.flush);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!expired) return null;
  const queued = items.filter((i) => i.status !== "failed");
  const hasDraft = draftHasContent(draft);
  const preserved = queued.length > 0 || hasDraft;

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ next: string }>(endpoints.auth.signIn, {
        method: "POST",
        body: { email: me.email, password },
        quiet401: true,
      });
      restore();
      setPassword("");
      if (r.next === "/two-factor") {
        void navigate({ to: "/two-factor" });
        return;
      }
      await qc.invalidateQueries();
      void flush();
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 401
          ? "That password doesn't match. Try again."
          : errorMessage(e),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={() => {}} label="Session expired">
      <div className="bg-brass px-5 py-3 font-mono text-[11px] uppercase tracking-[.14em] text-brass-ink">
        Session expired · nothing was lost
      </div>
      <div className="px-6 py-6">
        <div className="font-display text-[27px] font-semibold leading-tight">
          {preserved ? "Sign in to finish this booking" : "Sign in to carry on"}
        </div>
        <p className="mt-2 text-[15px] leading-[1.6] text-muted">
          You were away for a while.{" "}
          {preserved
            ? "What you were entering is still on this device and will be submitted the moment you're back in."
            : "Everything is where you left it."}
        </p>
        {preserved && (
          <div className="mt-4 rounded-md border-[1.5px] border-dashed border-brass bg-sunk p-3.5">
            <div className="eyebrow mb-2">Preserved</div>
            {queued.map((q) => (
              <div key={q.key} className="text-[15px] leading-[1.7]">
                {q.payload.guestName || "Guest"} · {q.payload.guestPhone || "—"}
                <br />
                {q.roomName} · {fmtDate(q.payload.checkIn)} →{" "}
                {fmtDate(q.payload.checkOut)}
                {q.payload.channel
                  ? ` · ${lookup(q.payload.channel).label}`
                  : ""}
              </div>
            ))}
            {hasDraft && queued.length === 0 && (
              <div className="text-[15px] leading-[1.7]">
                {draft.guestName || "Guest"} · {draft.guestPhone || "—"}
                {draft.checkIn && draft.checkOut && (
                  <>
                    <br />
                    {fmtDate(draft.checkIn)} → {fmtDate(draft.checkOut)}
                    {draft.channel ? ` · ${lookup(draft.channel).label}` : ""}
                  </>
                )}
              </div>
            )}
          </div>
        )}
        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void unlock();
          }}
        >
          <div className="font-mono text-[12px] text-subtle">{me.email}</div>
          <Input
            type="password"
            autoComplete="current-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {error && (
            <div className="rounded-md bg-[color-mix(in_srgb,var(--rhododendron)_9%,var(--surface))] px-3.5 py-2.5 text-[13.5px]">
              {error}
            </div>
          )}
          <Button
            type="submit"
            variant="primary"
            size="xl"
            loading={busy}
            disabled={!password}
          >
            {preserved ? "Unlock and submit" : "Unlock"}
          </Button>
          <button
            type="button"
            className="text-left text-[14px] text-deodar underline"
            onClick={() => void navigate({ to: "/sign-in" }).then(restore)}
          >
            Not {me.name.split(" ")[0]}? Sign in as someone else
          </button>
        </form>
      </div>
    </Dialog>
  );
}
